import { useEffect, useRef, useState, useContext } from 'react'
import { gsap } from 'gsap'
import { useRouter } from 'next/router'

// Context
import { PrintContext } from '../../context/PrintContext'

// Utils
import { createCookie } from '../../utils/store/cookies'
import { throttle } from '../../utils/helpers/throttle'

const PageTransition = () => {
  // router
  const router = useRouter()

  // context
  const printContext = useContext(PrintContext)

  // refs
  const componentRef = useRef()
  const overlayRef = useRef()

  // states
  const [isLocked, setIsLocked] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [loginReferer, setLoginReferer] = useState('/')

  useEffect(() => {
    document.body.classList.toggle('is-locked', isLocked)
  }, [isLocked])

  useEffect(() => {
    componentRef.current.classList.toggle('is-transitioning', isTransitioning)
  }, [isTransitioning])

  useEffect(() => {
    createCookie('login_referer', loginReferer, 30)
  }, [loginReferer])

  useEffect(() => {
    if (printContext.showFallBack) {
      startPageTransition()
    } else {
      endPageTransition()
    }
  }, [printContext?.showFallBack])

  useEffect(() => {
    const handleRouteChange = () => startPageTransition()
    const handleHistoryChange = (url) => endPageTransition(url)

    router.events.on('routeChangeStart', handleRouteChange)
    router.events.on('beforeHistoryChange', handleHistoryChange)
    window.addEventListener('resize', setVieportUnits)
    setVieportUnits()

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
      router.events.off('beforeHistoryChange', handleHistoryChange)
      window.removeEventListener('resize', setVieportUnits)
    }
  }, [])

  const setVieportUnits = throttle(() => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)

    const iw = window.innerWidth
    const cw = document.documentElement.clientWidth
    const diff = iw - cw
    const vw = `${(iw - diff) / 100}px`
    if (diff !== 0 || iw > cw) document.documentElement.style.setProperty('--vw', vw)
  }, 100)

  const startPageTransition = () => {
    // lock scroll
    setIsLocked(true)

    // show container
    setIsTransitioning(true)

    // show overlay
    gsap.set(overlayRef.current, { opacity: 0 })
    gsap.to(overlayRef.current, { duration: 0.3, opacity: 0.2 })
  }

  const endPageTransition = (url) => {
    // update login referer
    setLoginReferer(url)

    gsap.to(overlayRef.current, {
      duration: 0.3,
      opacity: 0,
      onComplete: () => {
        // hide container
        setIsTransitioning(false)
        // remove body lock
        setIsLocked(false)
      }
    })
  }

  return (
    <div className="PageTransition" ref={componentRef}>
      <div className="PageTransition-overlay" ref={overlayRef} />
    </div>
  )
}

export default PageTransition
